import React, { createContext, useContext, useState, useEffect } from 'react';
import Header from './components/Header.tsx';
import Hero from './components/Hero.tsx';
import Stats from './components/Stats.tsx';
import Features from './components/Features.tsx';
import Process from './components/Process.tsx';
import Testimonials from './components/Testimonials.tsx';
import TechStack from './components/TechStack.tsx';
import Faq from './components/Faq.tsx';
import Cta from './components/Cta.tsx';
import Footer from './components/Footer.tsx';
import CursorGlow from './components/CursorGlow.tsx';
import BackToTop from './components/BackToTop.tsx';

// GSAP, Lenis, and plugins are loaded from the CDN in index.html
declare const gsap: any;
declare const ScrollTrigger: any;

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>('light'); // Default to light

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as Theme | null;

    // Only use saved theme if it exists, otherwise stay light
    if (savedTheme) {
      setTheme(savedTheme);
    } else {
      // Force light mode by default and save it
      setTheme('light');
      localStorage.setItem('theme', 'light');
    }
  }, []);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const AppContent: React.FC = () => {
  const { theme } = useTheme();

  useEffect(() => {
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);

        // Reset scroll position on mount to prevent issues with browser restore
        if (window.location.hash === '' || window.location.hash === '#') {
          window.scrollTo(0, 0);
        }

        // Multiple refreshes with increasing delays to ensure proper initialization
        const timer1 = setTimeout(() => {
          ScrollTrigger.refresh();
        }, 100);

        const timer2 = setTimeout(() => {
          ScrollTrigger.refresh();
        }, 500);

        const timer3 = setTimeout(() => {
          ScrollTrigger.refresh();
        }, 1000);

        // Add window load event to refresh after everything is fully loaded
        const handleLoad = () => {
          setTimeout(() => {
            ScrollTrigger.refresh();
          }, 100);
        };

        if (document.readyState === 'complete') {
          handleLoad();
        } else {
          window.addEventListener('load', handleLoad);
        }

        return () => {
          clearTimeout(timer1);
          clearTimeout(timer2);
          clearTimeout(timer3);
          window.removeEventListener('load', handleLoad);
        };
    } else {
        console.warn('GSAP or ScrollTrigger not available.');
    }
  }, []);

  // Refresh ScrollTrigger when theme changes
  useEffect(() => {
    if (typeof ScrollTrigger !== 'undefined') {
      const timer = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [theme]);

  return (
    <div className="bg-gray-50 dark:bg-[#0A0A0A] text-gray-900 dark:text-gray-200 transition-colors duration-300" style={{ overflowX: 'clip' }}>
      {theme === 'dark' && <CursorGlow />}
      <Header />
      <main>
        <Hero />
        <Stats />
        <Features />
        <Process />
        <Testimonials />
        <TechStack />
        <Faq />
        <Cta />
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
};

export default App;